import type { PointHubDiagnostic } from './types';

export type PointHubDiagnosticSink = (diagnostic: Readonly<PointHubDiagnostic>) => void;

const DIAGNOSTIC_DEDUPE_MS = 60 * 1000;
const MAX_DEDUPE_KEYS = 100;
const recentDiagnostics = new Map<string, number>();
let diagnosticSink: PointHubDiagnosticSink | null = null;

const getDedupeKey = (diagnostic: PointHubDiagnostic): string => {
  return [diagnostic.code, diagnostic.eventId || '', diagnostic.field || '', diagnostic.environment || ''].join('|');
};

const pruneDedupeKeys = (now: number): void => {
  recentDiagnostics.forEach((timestamp, key) => {
    if (now - timestamp >= DIAGNOSTIC_DEDUPE_MS) recentDiagnostics.delete(key);
  });
  while (recentDiagnostics.size >= MAX_DEDUPE_KEYS) {
    const oldest = recentDiagnostics.keys().next();
    if (oldest.done) break;
    recentDiagnostics.delete(oldest.value);
  }
};

const shouldEmit = (diagnostic: PointHubDiagnostic, now: number): boolean => {
  pruneDedupeKeys(now);
  const key = getDedupeKey(diagnostic);
  const previous = recentDiagnostics.get(key);
  if (previous !== undefined && now - previous < DIAGNOSTIC_DEDUPE_MS) return false;
  recentDiagnostics.set(key, now);
  return true;
};

export const setPointHubDiagnosticSink = (sink: PointHubDiagnosticSink | null): void => {
  diagnosticSink = sink;
};

export const emitPointHubDiagnostic = (diagnostic: PointHubDiagnostic): void => {
  if (!diagnostic.code || !shouldEmit(diagnostic, Date.now())) return;
  if (diagnosticSink) {
    try {
      diagnosticSink(Object.freeze({ ...diagnostic }));
    } catch (_error) {
      // Diagnostics must never affect the booking flow.
    }
    return;
  }
  if (process.env.NODE_ENV !== 'production') {
    console.warn('[point-hub]', { ...diagnostic });
  }
};

export const resetPointHubDiagnosticsForTest = (): void => {
  recentDiagnostics.clear();
  diagnosticSink = null;
};
